import { useCallback } from 'react';
import { useNavigate } from '@remix-run/react';
import { BookmarkIcon, PhoneIcon } from 'lucide-react';
import { DetailsPageIsland } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIsland';
import { useProfessionalInitialRouteData } from '../../hook/use-professional-initial-route-data';
import { ReactComponent as EnquiryIcon } from '@rocco/icons/svg/message.svg';

import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { cn } from '@rocco/utils/cn';
import { useMatchesHandleData, useRootData } from '~/modules/root';
import { Button } from '@rocco/components/button';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { usePhoneTracking } from '~/modules/tracking/hook/usePhoneTracking';
import { FloatingIsland } from '../../../../../../rocco/src/ui/floating-island/components/FloatingIsland';

export const ProfessionalIsland = () => {
    const professional = useProfessionalInitialRouteData();
    const {
        CompanyLogo: companyLogo,
        LogoBackgroundColor: logoBackgroundColor,
        ID: professionalId,
        Title: professionalName,
        Categories,
        Link: professionalLink,
    } = professional;

    const { appDisplayMode } = useRootData();

    const {
        enabled,
        phone: phoneWithTracking,
        phoneLabel,
        handlePhoneClick,
    } = usePhoneTracking();

    const showPhoneTrackingCTA = enabled && !!phoneWithTracking;

    const isProfessionalPage = useMatchesHandleData(
        'isProfessionalProfile',
        false
    );
    const isOverviewPage = useMatchesHandleData('isOverviewPage', false);
    const navigate = useNavigate();

    const handleIslandClick = useCallback(() => {
        if (isOverviewPage) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } else if (isProfessionalPage) {
            navigate(professionalLink);
        }
    }, [isOverviewPage, isProfessionalPage, navigate, professionalLink]);

    const { openEnquiryModal } = useEnquiryModal({
        title: 'Send an Enquiry',
        professional: professional,
        itemType: 'professional',
        siteTreeID: professionalId,
        source: 'ProfessionalIsland',
        ctaLabel: 'Send an Enquiry',
        includeBranch: true,
    });

    if (appDisplayMode === DisplayMode.Mobile && !showPhoneTrackingCTA) {
        return null;
    }

    if (appDisplayMode === DisplayMode.Mobile && showPhoneTrackingCTA) {
        return (
            <FloatingIsland scrollThreshold={100} variant="transparent">
                <Button
                    type="button"
                    size={'default'}
                    color="primary"
                    onClick={handlePhoneClick}
                    className="w-full"
                >
                    <span className="mr-2.5 flex items-center">
                        {`Call us today`}
                    </span>
                    <PhoneIcon
                        aria-label={`Call us today`}
                        style={{
                            transform: 'scaleX(-1)',
                        }}
                        className="w-4 fill-black"
                    />
                </Button>
            </FloatingIsland>
        );
    }

    return (
        <DetailsPageIsland
            key={`professional-island-${professionalId}`}
            displayMode={appDisplayMode}
            scrollThreshold={0}
            image={{
                image: {
                    src: companyLogo,
                    alt: professionalName,
                },
                background: logoBackgroundColor,
                size: 'professionalLogo',
            }}
            title={professionalName}
            description={Categories[0]?.PrettyTitle ?? ''}
            onClick={handleIslandClick}
            primaryAction={
                <ProfessionalIslandEnquiryButton
                    label={'Send an Enquiry'}
                    onClick={openEnquiryModal}
                    displayMode={appDisplayMode}
                />
            }
            secondaryAction={
                showPhoneTrackingCTA ? (
                    <ProfessionalIslandCallButton
                        label={phoneLabel ?? 'Call Us'}
                        onClick={handlePhoneClick}
                        displayMode={appDisplayMode}
                    />
                ) : (
                    <ProfessionalIslandAddToDesignBoardButton
                        label="Shortlist this Pro"
                        displayMode={appDisplayMode}
                        professionalId={professionalId}
                    />
                )
            }
        />
    );
};

const ProfessionalIslandEnquiryButton = ({
    label,
    onClick,
    displayMode,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="primary"
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                <EnquiryIcon aria-label={label} className="w-4 fill-black" />
            </span>
        </Button>
    );
};

const ProfessionalIslandAddToDesignBoardButton = ({
    label,
    displayMode,
    professionalId,
}: {
    label: string;
    displayMode: DisplayMode;
    professionalId: number;
}) => {
    const compact = displayMode === DisplayMode.Mobile;
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const saved = isInDesignBoard(professionalId, 'ProfessionalPin');
    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="secondary70"
            onClick={() =>
                handleFollow(
                    { id: professionalId },
                    'ProfessionalPin',
                    'ProfessionalIsland'
                )
            }
        >
            <span className="flex items-center gap-2">
                {compact ? null : saved ? 'Shortlisted' : label}
                <BookmarkIcon
                    aria-label={label}
                    className={cn(
                        'w-4',
                        saved ? 'fill-gray-900' : 'stroke-gray-900'
                    )}
                />
            </span>
        </Button>
    );
};

const ProfessionalIslandCallButton = ({
    label,
    onClick,
    displayMode,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="white"
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                <PhoneIcon aria-label={label} className="w-4 fill-black" />
            </span>
        </Button>
    );
};
